import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { container } from '@/app/container';
import { TotvsRepositoryToken } from '../../di/tokens';
import type {
  CreateTotvsProductLinkInput,
  ListTotvsProductLinksInput,
  UpdateTotvsProductLinkInput,
} from '../../domain/entities/totvs.entity';

const PRODUCT_LINKS_QUERY_KEY = ['totvs', 'product-links'];

export function useTotvsProductLinks(input: ListTotvsProductLinksInput) {
  const repository = container.resolve(TotvsRepositoryToken);
  return useQuery({
    queryKey: [
      ...PRODUCT_LINKS_QUERY_KEY,
      input.page ?? 1,
      input.perPage ?? 10,
      input.provider ?? '',
      input.providerAccountId ?? '',
      input.productExternalId ?? '',
      input.offerExternalId ?? '',
      input.idAreaInteresse ?? '',
      input.codColigada ?? '',
      input.codFilial ?? '',
      input.idPs ?? '',
      input.enabled ?? '',
    ],
    queryFn: () => repository.listProductLinks(input),
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  });
}

function useInvalidateProductLinks() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({ queryKey: PRODUCT_LINKS_QUERY_KEY });
}

export function useCreateTotvsProductLink() {
  const repository = container.resolve(TotvsRepositoryToken);
  const invalidate = useInvalidateProductLinks();
  return useMutation({
    mutationFn: (input: CreateTotvsProductLinkInput) =>
      repository.createProductLink(input),
    onSuccess: invalidate,
  });
}

export function useUpdateTotvsProductLink() {
  const repository = container.resolve(TotvsRepositoryToken);
  const invalidate = useInvalidateProductLinks();
  return useMutation({
    mutationFn: (vars: { id: string; input: UpdateTotvsProductLinkInput }) =>
      repository.updateProductLink(vars.id, vars.input),
    onSuccess: invalidate,
  });
}

export function useDeleteTotvsProductLink() {
  const repository = container.resolve(TotvsRepositoryToken);
  const invalidate = useInvalidateProductLinks();
  return useMutation({
    mutationFn: (id: string) => repository.deleteProductLink(id),
    onSuccess: invalidate,
  });
}
